/**
 * switch-rotating-stack.js — Alpine component for the Rotating Stack block
 * (BLK-007, AST-008). The project's first Alpine component.
 *
 * Responsibility split (docs/FRONTEND-STACK.md, Decision #29):
 *   - Alpine (this file) : STATE + timing only — current item, paused,
 *     visibility, count, the auto-advance timer, pause-on-hover, the
 *     offscreen pause, the dots, and the Theme-Editor block-select jump.
 *   - GSAP (switch-animations.js → window.SwitchMotion.rotatingStackSwap) :
 *     ALL motion. Alpine hands a transition off to GSAP; it never tweens.
 *
 * Registered on `alpine:init` so it exists before Alpine boots (this script
 * is loaded `defer` BEFORE alpinejs.min.js in snippets/scripts.liquid).
 *
 * Fail-open: if SwitchMotion is absent (no GSAP) the swap is instant; if
 * Alpine never loads, the block's CSS shows every card stacked. Under
 * prefers-reduced-motion the component leaves that stacked fallback in
 * place and never rotates. This is the new-Switch-UI side of the Alpine/
 * Horizon boundary — it owns only its own markup, no Horizon elements.
 */

(function () {
  function register() {
    if (!window.Alpine) return;

    window.Alpine.data('switchRotatingStack', function (config) {
      config = config || {};
      return {
        interval: config.interval || 4000,
        duration: config.duration || 600,
        pauseOnHover: config.pauseOnHover !== false,
        showDots: config.showDots !== false,

        count: 0,
        current: 0,
        initialized: false,
        paused: false,
        visible: true,
        transitioning: false,
        motionOff: false,
        designMode: !!(window.Shopify && window.Shopify.designMode),

        _items: [],
        _timer: null,
        _io: null,

        init: function () {
          var self = this;
          var vp = this.$refs.viewport;
          if (!vp) return;

          // Items = the viewport's DIRECT children — whatever block type the
          // operator dropped in (this block accepts @theme/@app blocks + the
          // convenience item). Horizon blocks (e.g. `group`) emit a sibling
          // <style> tag for responsive padding right next to their root, so the
          // raw .children list is interleaved with non-content nodes. Filter
          // those out (STYLE/SCRIPT/TEMPLATE/LINK) — otherwise the count is
          // inflated and `is-active` can land on a <style> tag, leaving every
          // real card at opacity:0.
          this._items = Array.prototype.slice
            .call(vp.children)
            .filter(function (el) {
              return !/^(STYLE|SCRIPT|TEMPLATE|LINK)$/.test(el.tagName);
            });
          this.count = this._items.length;
          this.motionOff = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

          // 0–1 items, or reduced motion → keep the stacked CSS fallback
          // (every card visible, no rotation, no dots).
          if (this.count <= 1 || this.motionOff) return;

          // Single-view = a CSS grid stack (see the block's {% style %}); the
          // grid auto-sizes to the tallest item, so no JS height-lock needed.
          vp.classList.add('is-initialized');
          this._items.forEach(function (el, i) {
            el.classList.toggle('is-active', i === 0);
          });
          this.current = 0;
          this.initialized = true;

          // Theme Editor: no auto-rotation (it fights editing); instead jump
          // to whichever item block the operator selects.
          if (this.designMode) {
            document.addEventListener('shopify:block:select', function (e) {
              var idx = self._indexOfBlock(e.detail && e.detail.blockId);
              if (idx > -1) self.go(idx, true);
            });
            return;
          }

          this._observe();
          this._start();

          // Clean up if Alpine tears the component down (section re-render).
          this.$el.addEventListener(
            'alpine:destroyed',
            function () {
              self._stop();
              if (self._io) self._io.disconnect();
            },
            { once: true }
          );
        },

        // Match the editor-selected block to one of our items. Items can be any
        // block type, so check several places its id may surface: the
        // convenience item's data-block-id, the element's own id, or a
        // descendant carrying the id (native block roots vary).
        _indexOfBlock: function (blockId) {
          if (!blockId) return -1;
          for (var i = 0; i < this._items.length; i++) {
            var el = this._items[i];
            var dataId = el.getAttribute('data-block-id');
            if (dataId && blockId.indexOf(dataId) !== -1) return i;
            if (el.id && el.id.indexOf(blockId) !== -1) return i;
            if (el.querySelector && el.querySelector('[id*="' + blockId + '"]')) return i;
          }
          return -1;
        },

        _observe: function () {
          var self = this;
          if (!('IntersectionObserver' in window)) return;
          this._io = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (en) {
                self.visible = en.isIntersecting;
              });
            },
            { threshold: 0.2 }
          );
          this._io.observe(this.$el);
        },

        _start: function () {
          var self = this;
          this._stop();
          this._timer = setInterval(function () {
            if (self.paused || !self.visible || self.transitioning) return;
            self.next();
          }, this.interval);
        },

        _stop: function () {
          if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
          }
        },

        onEnter: function () {
          if (this.pauseOnHover) this.paused = true;
        },
        onLeave: function () {
          if (this.pauseOnHover) this.paused = false;
        },

        next: function () {
          this.go((this.current + 1) % this.count);
        },

        // index: target item. manual: came from a dot/editor → reset timer.
        go: function (index, manual) {
          var self = this;
          if (this.transitioning || index === this.current || index < 0 || index >= this.count) {
            return;
          }
          var from = this._items[this.current];
          var to = this._items[index];
          this.transitioning = true;

          // Direction for the blur tween's vertical drift (wrap-aware).
          var dir = index > this.current ? 'next' : 'prev';
          if (this.current === this.count - 1 && index === 0) dir = 'next';
          if (this.current === 0 && index === this.count - 1) dir = 'prev';

          var swap = function () {
            from.classList.remove('is-active');
            to.classList.add('is-active');
            self.current = index;
            self.transitioning = false;
            if (manual && !self.designMode) self._start(); // reset interval
          };

          var motion = window.SwitchMotion && window.SwitchMotion.rotatingStackSwap;
          if (motion) {
            motion(this.$refs.viewport, from, to, { duration: this.duration, direction: dir }).then(swap);
          } else {
            swap();
          }
        },
      };
    });
  }

  document.addEventListener('alpine:init', register);
})();
